import { defineNuxtPlugin } from '#app'
import { DatabaseViewsAdminType } from '@grouper/modules/database/adminTypes'
import { DatabaseApplicationType } from '@grouper/modules/database/applicationTypes'
import {
  DuplicateTableJobType,
  SyncDataSyncTableJobType,
  FileImportJobType,
  DuplicateFieldJobType,
  AirtableJobType,
} from '@grouper/modules/database/jobTypes'
import {
  GridViewType,
  GalleryViewType,
  FormViewType,
} from '@grouper/modules/database/viewTypes'
import {
  TextFieldType,
  LongTextFieldType,
  URLFieldType,
  EmailFieldType,
  LinkRowFieldType,
  NumberFieldType,
  RatingFieldType,
  BooleanFieldType,
  DateFieldType,
  LastModifiedFieldType,
  LastModifiedByFieldType,
  FileFieldType,
  SingleSelectFieldType,
  MultipleSelectFieldType,
  PhoneNumberFieldType,
  CreatedOnFieldType,
  CreatedByFieldType,
  DurationFieldType,
  FormulaFieldType,
  CountFieldType,
  RollupFieldType,
  LookupFieldType,
  MultipleCollaboratorsFieldType,
  UUIDFieldType,
  AutonumberFieldType,
  PasswordFieldType,
  FormViewEditRowFieldType,
  ButtonFieldType,
} from '@grouper/modules/database/fieldTypes'
import {
  EqualViewFilterType,
  NotEqualViewFilterType,
  ContainsViewFilterType,
  FilenameContainsViewFilterType,
  FilesLowerThanViewFilterType,
  HasFileTypeViewFilterType,
  ContainsNotViewFilterType,
  LengthIsLowerThanViewFilterType,
  HigherThanViewFilterType,
  HigherThanOrEqualViewFilterType,
  LowerThanViewFilterType,
  LowerThanOrEqualViewFilterType,
  IsEvenAndWholeViewFilterType,
  SingleSelectEqualViewFilterType,
  SingleSelectNotEqualViewFilterType,
  SingleSelectIsAnyOfViewFilterType,
  SingleSelectIsNoneOfViewFilterType,
  BooleanViewFilterType,
  EmptyViewFilterType,
  NotEmptyViewFilterType,
  LinkRowHasFilterType,
  LinkRowHasNotFilterType,
  MultipleSelectHasFilterType,
  MultipleSelectHasNotFilterType,
  MultipleCollaboratorsHasFilterType,
  MultipleCollaboratorsHasNotFilterType,
  LinkRowContainsFilterType,
  LinkRowNotContainsFilterType,
  ContainsWordViewFilterType,
  DoesntContainWordViewFilterType,
  StartsWithViewFilterType,
  UserIsFilterType,
  UserIsNotFilterType,
  DateIsEqualMultiStepViewFilterType,
  DateIsBeforeMultiStepViewFilterType,
  DateIsOnOrBeforeMultiStepViewFilterType,
  DateIsAfterMultiStepViewFilterType,
  DateIsOnOrAfterMultiStepViewFilterType,
  DateIsWithinMultiStepViewFilterType,
  DateIsNotEqualMultiStepViewFilterType,
  // Deprecated date filter types
  DateEqualViewFilterType,
  DateNotEqualViewFilterType,
  DateEqualsTodayViewFilterType,
  DateBeforeTodayViewFilterType,
  DateAfterTodayViewFilterType,
  DateWithinDaysViewFilterType,
  DateWithinWeeksViewFilterType,
  DateWithinMonthsViewFilterType,
  DateEqualsDaysAgoViewFilterType,
  DateEqualsMonthsAgoViewFilterType,
  DateEqualsYearsAgoViewFilterType,
  DateEqualsCurrentWeekViewFilterType,
  DateEqualsCurrentMonthViewFilterType,
  DateEqualsCurrentYearViewFilterType,
  DateBeforeViewFilterType,
  DateBeforeOrEqualViewFilterType,
  DateAfterDaysAgoViewFilterType,
  DateAfterViewFilterType,
  DateAfterOrEqualViewFilterType,
  DateEqualsDayOfMonthViewFilterType,
} from '@grouper/modules/database/viewFilters'
import {
  HasValueEqualViewFilterType,
  HasEmptyValueViewFilterType,
  HasNotEmptyValueViewFilterType,
  HasNotValueEqualViewFilterType,
  HasValueContainsViewFilterType,
  HasNotValueContainsViewFilterType,
  HasValueContainsWordViewFilterType,
  HasNotValueContainsWordViewFilterType,
  HasValueLengthIsLowerThanViewFilterType,
  HasAllValuesEqualViewFilterType,
  HasAnySelectOptionEqualViewFilterType,
  HasNoneSelectOptionEqualViewFilterType,
  HasValueLowerThanViewFilterType,
  HasValueLowerThanOrEqualViewFilterType,
  HasValueHigherThanViewFilterType,
  HasValueHigherThanOrEqualViewFilterType,
  HasNotValueLowerThanOrEqualViewFilterType,
  HasNotValueLowerThanViewFilterType,
  HasNotValueHigherThanOrEqualViewFilterType,
  HasNotValueHigherThanViewFilterType,
  HasDateEqualViewFilterType,
  HasNotDateEqualViewFilterType,
  HasDateBeforeViewFilterType,
  HasNotDateBeforeViewFilterType,
  HasDateOnOrBeforeViewFilterType,
  HasNotDateOnOrBeforeViewFilterType,
  HasDateAfterViewFilterType,
  HasNotDateAfterViewFilterType,
  HasDateOnOrAfterViewFilterType,
  HasNotDateOnOrAfterViewFilterType,
  HasDateWithinViewFilterType,
  HasNotDateWithinViewFilterType,
} from '@grouper/modules/database/arrayViewFilters'
import {
  CSVImporterType,
  PasteImporterType,
  XMLImporterType,
  JSONImporterType,
  ExcelImporterType,
} from '@grouper/modules/database/importerTypes'
import {
  ICalCalendarDataSyncType,
  PostgreSQLDataSyncType,
} from '@grouper/modules/database/dataSyncTypes'
import {
  RowsCreatedWebhookEventType,
  RowsUpdatedWebhookEventType,
  RowsDeletedWebhookEventType,
  FieldCreatedWebhookEventType,
  FieldUpdatedWebhookEventType,
  FieldDeletedWebhookEventType,
  ViewCreatedWebhookEventType,
  ViewUpdatedWebhookEventType,
  ViewDeletedWebhookEventType,
} from '@grouper/modules/database/webhookEventTypes'
import {
  ImageFilePreview,
  AudioFilePreview,
  VideoFilePreview,
  PDFBrowserFilePreview,
  GoogleDocFilePreview,
} from '@grouper/modules/database/filePreviewTypes'

import {
  TextTypeUniqueWithEmptyConstraintType,
  RatingTypeUniqueWithEmptyConstraintType,
  GenericUniqueWithEmptyConstraintType,
} from '@grouper/modules/database/fieldConstraintTypes'

import { APITokenSettingsType } from '@grouper/modules/database/settingsTypes'

import { CSVTableExporterType } from '@grouper/modules/database/exporterTypes'
import {
  GrouperAdd,
  GrouperAnd,
  GrouperConcat,
  GrouperDateDiff,
  GrouperDateInterval,
  GrouperDatetimeFormat,
  GrouperDatetimeFormatTz,
  GrouperDay,
  GrouperDivide,
  GrouperEncodeUri,
  GrouperEncodeUriComponent,
  GrouperEqual,
  GrouperHasOption,
  GrouperField,
  GrouperSearch,
  GrouperGreaterThan,
  GrouperGreaterThanOrEqual,
  GrouperIf,
  GrouperIsBlank,
  GrouperIsNull,
  GrouperDurationToSeconds,
  GrouperSecondsToDuration,
  GrouperLessThan,
  GrouperLessThanOrEqual,
  GrouperLower,
  GrouperSplitPart,
  GrouperMinus,
  GrouperMultiply,
  GrouperNot,
  GrouperOr,
  GrouperReplace,
  GrouperRowId,
  GrouperT,
  GrouperNow,
  GrouperToday,
  GrouperToDateTz,
  GrouperToDate,
  GrouperToNumber,
  GrouperToText,
  GrouperUpper,
  GrouperReverse,
  GrouperLength,
  GrouperNotEqual,
  GrouperLookup,
  GrouperSum,
  GrouperAvg,
  GrouperVariancePop,
  GrouperVarianceSample,
  GrouperStddevSample,
  GrouperStddevPop,
  GrouperJoin,
  GrouperCount,
  GrouperMin,
  GrouperMax,
  GrouperEvery,
  GrouperAny,
  GrouperWhenEmpty,
  GrouperSecond,
  GrouperYear,
  GrouperMonth,
  GrouperLeast,
  GrouperGreatest,
  GrouperRegexReplace,
  GrouperLink,
  GrouperTrim,
  GrouperRight,
  GrouperLeft,
  GrouperContains,
  GrouperFilter,
  GrouperTrunc,
  GrouperIsNaN,
  GrouperWhenNaN,
  GrouperEven,
  GrouperOdd,
  GrouperCeil,
  GrouperFloor,
  GrouperAbs,
  GrouperExp,
  GrouperLn,
  GrouperSign,
  GrouperSqrt,
  GrouperRound,
  GrouperLog,
  GrouperPower,
  GrouperMod,
  GrouperButton,
  GrouperGetLinkUrl,
  GrouperGetLinkLabel,
  GrouperIsImage,
  GrouperGetImageHeight,
  GrouperGetImageWidth,
  GrouperGetFileSize,
  GrouperGetFileMimeType,
  GrouperGetFileVisibleName,
  GrouperIndex,
  GrouperGetFileCount,
  GrouperToUrl,
  GrouperArrayUnique,
  GrouperArraySlice,
  GrouperFirst,
  GrouperLast,
} from '@grouper/modules/database/formula/functions'
import {
  GrouperFormulaArrayType,
  GrouperFormulaBooleanType,
  GrouperFormulaButtonType,
  GrouperFormulaCharType,
  GrouperFormulaLinkType,
  GrouperFormulaDateIntervalType, // Deprecated
  GrouperFormulaDurationType,
  GrouperFormulaDateType,
  GrouperFormulaInvalidType,
  GrouperFormulaNumberType,
  GrouperFormulaSingleSelectType,
  GrouperFormulaMultipleSelectType,
  GrouperFormulaMultipleCollaboratorsType,
  GrouperFormulaSpecialType,
  GrouperFormulaTextType,
  GrouperFormulaFileType,
  GrouperFormulaURLType,
} from '@grouper/modules/database/formula/formulaTypes'
import {
  CountViewAggregationType,
  EmptyCountViewAggregationType,
  NotEmptyCountViewAggregationType,
  CheckedCountViewAggregationType,
  NotCheckedCountViewAggregationType,
  EmptyPercentageViewAggregationType,
  NotEmptyPercentageViewAggregationType,
  CheckedPercentageViewAggregationType,
  NotCheckedPercentageViewAggregationType,
  UniqueCountViewAggregationType,
  MinViewAggregationType,
  MaxViewAggregationType,
  EarliestDateViewAggregationType,
  LatestDateViewAggregationType,
  SumViewAggregationType,
  AverageViewAggregationType,
  StdDevViewAggregationType,
  VarianceViewAggregationType,
  MedianViewAggregationType,
  DistributionViewAggregationType,
} from '@grouper/modules/database/viewAggregationTypes'
import { FormViewFormModeType } from '@grouper/modules/database/formViewModeTypes'
import { CollaborativeViewOwnershipType } from '@grouper/modules/database/viewOwnershipTypes'
import {
  CollaboratorAddedToRowNotificationType,
  FormSubmittedNotificationType,
  UserMentionInRichTextFieldNotificationType,
  WebhookDeactivatedNotificationType,
  WebhookPayloadTooLargedNotificationType,
} from '@grouper/modules/database/notificationTypes'
import { HistoryRowModalSidebarType } from '@grouper/modules/database/rowModalSidebarTypes'
import { FieldsDataProviderType } from '@grouper/modules/database/dataProviderTypes'

import {
  DatabaseOnboardingType,
  DatabaseScratchTrackOnboardingType,
  DatabaseImportOnboardingType,
  DatabaseScratchTrackFieldsOnboardingType,
} from '@grouper/modules/database/onboardingTypes'

import {
  ScratchDatabaseOnboardingStepType,
  ImportDatabaseOnboardingStepType,
  AirtableDatabaseOnboardingStepType,
} from '@grouper/modules/database/databaseOnboardingStepTypes'

import {
  DatabaseScratchTrackCampaignFieldsOnboardingType,
  DatabaseScratchTrackCustomFieldsOnboardingType,
  DatabaseScratchTrackProjectFieldsOnboardingType,
  DatabaseScratchTrackTaskFieldsOnboardingType,
  DatabaseScratchTrackTeamFieldsOnboardingType,
} from '@grouper/modules/database/databaseScratchTrackFieldsStepType'
import {
  SyncedFieldsConfigureDataSyncType,
  SettingsConfigureDataSyncType,
  SyncHistoryConfigureDataSyncType,
} from '@grouper/modules/database/configureDataSyncTypes'
import { DatabaseGuidedTourType } from '@grouper/modules/database/guidedTourTypes'
import {
  DatabaseSearchType,
  DatabaseTableSearchType,
  DatabaseFieldSearchType,
  DatabaseRowSearchType,
} from '@grouper/modules/database/searchTypes'
import { searchTypeRegistry } from '@grouper/modules/core/search/types/registry'

export default defineNuxtPlugin({
  name: 'database',
  dependsOn: ['core'],
  setup(nuxtApp) {
    const { $registry } = nuxtApp

    const context = { app: nuxtApp }

    $registry.registerNamespace('viewDecorator')
    $registry.registerNamespace('decoratorValueProvider')
    $registry.registerNamespace('twoWaySyncStrategy')
    $registry.registerNamespace('viewFilter')
    $registry.registerNamespace('viewOwnershipType')
    $registry.registerNamespace('fieldConstraint')
    $registry.registerNamespace('importer')
    $registry.registerNamespace('exporter')
    $registry.registerNamespace('dataSync')
    $registry.registerNamespace('webhookEvent')
    $registry.registerNamespace('formula_function')
    $registry.registerNamespace('formula_type')
    $registry.registerNamespace('preview')
    $registry.registerNamespace('viewAggregation')
    $registry.registerNamespace('formViewMode')
    $registry.registerNamespace('databaseDataProvider')
    $registry.registerNamespace('rowModalSidebar')
    $registry.registerNamespace('onboardingTrackFields')
    $registry.registerNamespace('configureDataSync')
    $registry.registerNamespace('databaseOnboardingStep')

    $registry.register('application', new DatabaseApplicationType(context))
    $registry.register('admin', new DatabaseViewsAdminType(context))

    $registry.register('job', new DuplicateTableJobType(context))
    $registry.register('job', new SyncDataSyncTableJobType(context))
    $registry.register('job', new FileImportJobType(context))
    $registry.register('job', new DuplicateFieldJobType(context))
    $registry.register('job', new AirtableJobType(context))

    $registry.register('view', new GridViewType(context))
    $registry.register('view', new GalleryViewType(context))
    $registry.register('view', new FormViewType(context))
    $registry.register('viewFilter', new EqualViewFilterType(context))
    $registry.register('viewFilter', new NotEqualViewFilterType(context))
    $registry.register(
      'viewFilter',
      new DateIsEqualMultiStepViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateIsNotEqualMultiStepViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateIsBeforeMultiStepViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateIsOnOrBeforeMultiStepViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateIsAfterMultiStepViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateIsOnOrAfterMultiStepViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateIsWithinMultiStepViewFilterType(context)
    )
    // DEPRECATED
    $registry.register('viewFilter', new DateEqualViewFilterType(context))
    $registry.register('viewFilter', new DateNotEqualViewFilterType(context))
    $registry.register('viewFilter', new DateEqualsTodayViewFilterType(context))
    $registry.register('viewFilter', new DateBeforeTodayViewFilterType(context))
    $registry.register('viewFilter', new DateAfterTodayViewFilterType(context))
    $registry.register('viewFilter', new DateWithinDaysViewFilterType(context))
    $registry.register('viewFilter', new DateWithinWeeksViewFilterType(context))
    $registry.register(
      'viewFilter',
      new DateWithinMonthsViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateEqualsDaysAgoViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateEqualsMonthsAgoViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateEqualsYearsAgoViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateEqualsCurrentWeekViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateEqualsCurrentMonthViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateEqualsCurrentYearViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateEqualsDayOfMonthViewFilterType(context)
    )
    $registry.register('viewFilter', new DateBeforeViewFilterType(context))
    $registry.register(
      'viewFilter',
      new DateBeforeOrEqualViewFilterType(context)
    )
    $registry.register('viewFilter', new DateAfterViewFilterType(context))
    $registry.register(
      'viewFilter',
      new DateAfterOrEqualViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new DateAfterDaysAgoViewFilterType(context)
    )
    // END
    $registry.register('viewFilter', new HasEmptyValueViewFilterType(context))
    $registry.register(
      'viewFilter',
      new HasNotEmptyValueViewFilterType(context)
    )
    $registry.register('viewFilter', new HasValueEqualViewFilterType(context))
    $registry.register(
      'viewFilter',
      new HasNotValueEqualViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasValueContainsViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasNotValueContainsViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasValueContainsWordViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasNotValueContainsWordViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasValueLengthIsLowerThanViewFilterType(context)
    )
    $registry.register(
      'viewFilter',

      new HasAllValuesEqualViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasAnySelectOptionEqualViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasNoneSelectOptionEqualViewFilterType(context)
    )
    $registry.register('viewFilter', new ContainsViewFilterType(context))
    $registry.register('viewFilter', new ContainsNotViewFilterType(context))
    $registry.register('viewFilter', new ContainsWordViewFilterType(context))
    $registry.register(
      'viewFilter',
      new DoesntContainWordViewFilterType(context)
    )
    $registry.register('viewFilter', new StartsWithViewFilterType(context))
    $registry.register(
      'viewFilter',
      new FilenameContainsViewFilterType(context)
    )
    $registry.register('viewFilter', new HasFileTypeViewFilterType(context))
    $registry.register('viewFilter', new FilesLowerThanViewFilterType(context))
    $registry.register(
      'viewFilter',
      new LengthIsLowerThanViewFilterType(context)
    )
    $registry.register('viewFilter', new HigherThanViewFilterType(context))
    $registry.register(
      'viewFilter',
      new HigherThanOrEqualViewFilterType(context)
    )
    $registry.register('viewFilter', new LowerThanViewFilterType(context))
    $registry.register(
      'viewFilter',
      new LowerThanOrEqualViewFilterType(context)
    )
    $registry.register('viewFilter', new IsEvenAndWholeViewFilterType(context))
    $registry.register(
      'viewFilter',
      new SingleSelectEqualViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new SingleSelectNotEqualViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new SingleSelectIsAnyOfViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new SingleSelectIsNoneOfViewFilterType(context)
    )

    $registry.register('viewFilter', new BooleanViewFilterType(context))
    $registry.register('viewFilter', new LinkRowHasFilterType(context))
    $registry.register('viewFilter', new LinkRowHasNotFilterType(context))
    $registry.register('viewFilter', new LinkRowContainsFilterType(context))
    $registry.register('viewFilter', new LinkRowNotContainsFilterType(context))
    $registry.register('viewFilter', new MultipleSelectHasFilterType(context))
    $registry.register(
      'viewFilter',
      new MultipleSelectHasNotFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new MultipleCollaboratorsHasFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new MultipleCollaboratorsHasNotFilterType(context)
    )
    $registry.register('viewFilter', new EmptyViewFilterType(context))
    $registry.register('viewFilter', new NotEmptyViewFilterType(context))
    $registry.register('viewFilter', new UserIsFilterType(context))
    $registry.register('viewFilter', new UserIsNotFilterType(context))
    $registry.register(
      'viewFilter',
      new HasValueHigherThanViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasNotValueHigherThanViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasValueHigherThanOrEqualViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasNotValueHigherThanOrEqualViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasValueLowerThanViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasNotValueLowerThanViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasValueLowerThanOrEqualViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasNotValueLowerThanOrEqualViewFilterType(context)
    )
    $registry.register('viewFilter', new HasDateEqualViewFilterType(context))
    $registry.register('viewFilter', new HasNotDateEqualViewFilterType(context))
    $registry.register('viewFilter', new HasDateBeforeViewFilterType(context))
    $registry.register(
      'viewFilter',
      new HasNotDateBeforeViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasDateOnOrBeforeViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasNotDateOnOrBeforeViewFilterType(context)
    )
    $registry.register('viewFilter', new HasDateAfterViewFilterType(context))
    $registry.register('viewFilter', new HasNotDateAfterViewFilterType(context))
    $registry.register(
      'viewFilter',
      new HasDateOnOrAfterViewFilterType(context)
    )
    $registry.register(
      'viewFilter',
      new HasNotDateOnOrAfterViewFilterType(context)
    )
    $registry.register('viewFilter', new HasDateWithinViewFilterType(context))
    $registry.register(
      'viewFilter',
      new HasNotDateWithinViewFilterType(context)
    )

    $registry.register(
      'viewOwnershipType',
      new CollaborativeViewOwnershipType(context)
    )

    $registry.register('field', new TextFieldType(context))
    $registry.register('field', new LongTextFieldType(context))
    $registry.register('field', new LinkRowFieldType(context))
    $registry.register('field', new NumberFieldType(context))
    $registry.register('field', new RatingFieldType(context))
    $registry.register('field', new BooleanFieldType(context))
    $registry.register('field', new DateFieldType(context))
    $registry.register('field', new LastModifiedFieldType(context))
    $registry.register('field', new LastModifiedByFieldType(context))
    $registry.register('field', new CreatedOnFieldType(context))
    $registry.register('field', new CreatedByFieldType(context))
    $registry.register('field', new DurationFieldType(context))
    $registry.register('field', new URLFieldType(context))
    $registry.register('field', new EmailFieldType(context))
    $registry.register('field', new FileFieldType(context))
    $registry.register('field', new SingleSelectFieldType(context))
    $registry.register('field', new MultipleSelectFieldType(context))
    $registry.register('field', new PhoneNumberFieldType(context))
    $registry.register('field', new FormulaFieldType(context))
    $registry.register('field', new CountFieldType(context))
    $registry.register('field', new RollupFieldType(context))
    $registry.register('field', new LookupFieldType(context))
    $registry.register('field', new MultipleCollaboratorsFieldType(context))
    $registry.register('field', new UUIDFieldType(context))
    $registry.register('field', new AutonumberFieldType(context))
    $registry.register('field', new PasswordFieldType(context))
    $registry.register('field', new FormViewEditRowFieldType(context))
    // Always registered so existing button fields keep rendering when the
    // flag is off; the field type dropdown hides it via isVisibleInDropdown.
    $registry.register('field', new ButtonFieldType(context))

    $registry.register(
      'fieldConstraint',
      new TextTypeUniqueWithEmptyConstraintType(context)
    )
    $registry.register(
      'fieldConstraint',
      new RatingTypeUniqueWithEmptyConstraintType(context)
    )
    $registry.register(
      'fieldConstraint',
      new GenericUniqueWithEmptyConstraintType(context)
    )

    $registry.register('importer', new CSVImporterType(context))
    $registry.register('importer', new PasteImporterType(context))
    $registry.register('importer', new XMLImporterType(context))
    $registry.register('importer', new JSONImporterType(context))
    $registry.register('importer', new ExcelImporterType(context))
    $registry.register('dataSync', new ICalCalendarDataSyncType(context))
    $registry.register('dataSync', new PostgreSQLDataSyncType(context))
    $registry.register('settings', new APITokenSettingsType(context))
    $registry.register('exporter', new CSVTableExporterType(context))
    $registry.register('webhookEvent', new RowsCreatedWebhookEventType(context))
    $registry.register('webhookEvent', new RowsUpdatedWebhookEventType(context))
    $registry.register('webhookEvent', new RowsDeletedWebhookEventType(context))
    $registry.register(
      'webhookEvent',
      new FieldCreatedWebhookEventType(context)
    )
    $registry.register(
      'webhookEvent',
      new FieldUpdatedWebhookEventType(context)
    )
    $registry.register(
      'webhookEvent',
      new FieldDeletedWebhookEventType(context)
    )
    $registry.register('webhookEvent', new ViewCreatedWebhookEventType(context))
    $registry.register('webhookEvent', new ViewUpdatedWebhookEventType(context))
    $registry.register('webhookEvent', new ViewDeletedWebhookEventType(context))

    // Text functions
    $registry.register('formula_function', new GrouperUpper(context))
    $registry.register('formula_function', new GrouperLower(context))
    $registry.register('formula_function', new GrouperConcat(context))
    $registry.register('formula_function', new GrouperToText(context))
    $registry.register('formula_function', new GrouperT(context))
    $registry.register('formula_function', new GrouperReplace(context))
    $registry.register('formula_function', new GrouperSearch(context))
    $registry.register('formula_function', new GrouperLength(context))
    $registry.register('formula_function', new GrouperReverse(context))
    $registry.register('formula_function', new GrouperEncodeUri(context))
    $registry.register(
      'formula_function',
      new GrouperEncodeUriComponent(context)
    )
    $registry.register('formula_function', new GrouperSplitPart(context))
    // Number functions
    $registry.register('formula_function', new GrouperMultiply(context))
    $registry.register('formula_function', new GrouperDivide(context))
    $registry.register('formula_function', new GrouperToNumber(context))
    // Boolean functions
    $registry.register('formula_function', new GrouperIf(context))
    $registry.register('formula_function', new GrouperEqual(context))
    $registry.register('formula_function', new GrouperHasOption(context))
    $registry.register('formula_function', new GrouperIsBlank(context))
    $registry.register('formula_function', new GrouperIsNull(context))
    $registry.register('formula_function', new GrouperNot(context))
    $registry.register('formula_function', new GrouperNotEqual(context))
    $registry.register('formula_function', new GrouperGreaterThan(context))
    $registry.register(
      'formula_function',
      new GrouperGreaterThanOrEqual(context)
    )
    $registry.register('formula_function', new GrouperLessThan(context))
    $registry.register('formula_function', new GrouperLessThanOrEqual(context))
    $registry.register('formula_function', new GrouperAnd(context))
    $registry.register('formula_function', new GrouperOr(context))
    // Date functions
    $registry.register('formula_function', new GrouperDatetimeFormat(context))
    $registry.register('formula_function', new GrouperDatetimeFormatTz(context))
    $registry.register('formula_function', new GrouperDay(context))
    $registry.register('formula_function', new GrouperNow(context))
    $registry.register('formula_function', new GrouperToday(context))
    $registry.register('formula_function', new GrouperToDateTz(context))
    $registry.register('formula_function', new GrouperToDate(context))
    $registry.register('formula_function', new GrouperDateDiff(context))
    // Date interval functions
    $registry.register('formula_function', new GrouperDateInterval(context))
    $registry.register(
      'formula_function',
      new GrouperDurationToSeconds(context)
    )
    $registry.register(
      'formula_function',
      new GrouperSecondsToDuration(context)
    )
    // Special functions. NOTE: rollup compatible functions are shown field sub-form in
    // the same order as they are listed here.
    $registry.register('formula_function', new GrouperAdd(context))
    $registry.register('formula_function', new GrouperMinus(context))
    $registry.register('formula_function', new GrouperField(context))
    $registry.register('formula_function', new GrouperLookup(context))
    $registry.register('formula_function', new GrouperRowId(context))
    $registry.register('formula_function', new GrouperContains(context))
    $registry.register('formula_function', new GrouperLeft(context))
    $registry.register('formula_function', new GrouperRight(context))
    $registry.register('formula_function', new GrouperTrim(context))
    $registry.register('formula_function', new GrouperRegexReplace(context))
    $registry.register('formula_function', new GrouperGreatest(context))
    $registry.register('formula_function', new GrouperLeast(context))
    $registry.register('formula_function', new GrouperMonth(context))
    $registry.register('formula_function', new GrouperYear(context))
    $registry.register('formula_function', new GrouperSecond(context))
    $registry.register('formula_function', new GrouperWhenEmpty(context))
    $registry.register('formula_function', new GrouperAny(context))
    $registry.register('formula_function', new GrouperEvery(context))
    $registry.register('formula_function', new GrouperMin(context))
    $registry.register('formula_function', new GrouperMax(context))
    $registry.register('formula_function', new GrouperCount(context))
    $registry.register('formula_function', new GrouperSum(context))
    $registry.register('formula_function', new GrouperAvg(context))
    $registry.register('formula_function', new GrouperJoin(context))
    $registry.register('formula_function', new GrouperStddevPop(context))
    $registry.register('formula_function', new GrouperStddevSample(context))
    $registry.register('formula_function', new GrouperVarianceSample(context))
    $registry.register('formula_function', new GrouperVariancePop(context))
    $registry.register('formula_function', new GrouperFilter(context))
    $registry.register('formula_function', new GrouperTrunc(context))
    $registry.register('formula_function', new GrouperIsNaN(context))
    $registry.register('formula_function', new GrouperWhenNaN(context))
    $registry.register('formula_function', new GrouperEven(context))
    $registry.register('formula_function', new GrouperOdd(context))
    $registry.register('formula_function', new GrouperAbs(context))
    $registry.register('formula_function', new GrouperCeil(context))
    $registry.register('formula_function', new GrouperFloor(context))
    $registry.register('formula_function', new GrouperSign(context))
    $registry.register('formula_function', new GrouperLog(context))
    $registry.register('formula_function', new GrouperExp(context))
    $registry.register('formula_function', new GrouperLn(context))
    $registry.register('formula_function', new GrouperPower(context))
    $registry.register('formula_function', new GrouperSqrt(context))
    $registry.register('formula_function', new GrouperRound(context))
    $registry.register('formula_function', new GrouperMod(context))
    // Link functions
    $registry.register('formula_function', new GrouperLink(context))
    $registry.register('formula_function', new GrouperButton(context))
    $registry.register('formula_function', new GrouperGetLinkUrl(context))
    $registry.register('formula_function', new GrouperGetLinkLabel(context))
    // File functions
    $registry.register(
      'formula_function',
      new GrouperGetFileVisibleName(context)
    )
    $registry.register('formula_function', new GrouperGetFileMimeType(context))
    $registry.register('formula_function', new GrouperGetFileSize(context))
    $registry.register('formula_function', new GrouperGetImageWidth(context))
    $registry.register('formula_function', new GrouperGetImageHeight(context))
    $registry.register('formula_function', new GrouperIsImage(context))

    $registry.register('formula_function', new GrouperGetFileCount(context))
    $registry.register('formula_function', new GrouperIndex(context))
    $registry.register('formula_function', new GrouperToUrl(context))
    $registry.register('formula_function', new GrouperArrayUnique(context))
    $registry.register('formula_function', new GrouperArraySlice(context))
    $registry.register('formula_function', new GrouperFirst(context))
    $registry.register('formula_function', new GrouperLast(context))

    // Formula Types
    $registry.register('formula_type', new GrouperFormulaTextType(context))
    $registry.register('formula_type', new GrouperFormulaCharType(context))
    $registry.register('formula_type', new GrouperFormulaBooleanType(context))
    $registry.register('formula_type', new GrouperFormulaDateType(context))
    $registry.register(
      'formula_type',
      new GrouperFormulaDateIntervalType(context)
    )
    $registry.register('formula_type', new GrouperFormulaDurationType(context))
    $registry.register('formula_type', new GrouperFormulaNumberType(context))
    $registry.register('formula_type', new GrouperFormulaArrayType(context))
    $registry.register('formula_type', new GrouperFormulaSpecialType(context))
    $registry.register('formula_type', new GrouperFormulaInvalidType(context))
    $registry.register(
      'formula_type',
      new GrouperFormulaSingleSelectType(context)
    )
    $registry.register('formula_type', new GrouperFormulaURLType(context))
    $registry.register(
      'formula_type',
      new GrouperFormulaMultipleSelectType(context)
    )
    $registry.register('formula_type', new GrouperFormulaButtonType(context))
    $registry.register('formula_type', new GrouperFormulaLinkType(context))
    $registry.register('formula_type', new GrouperFormulaFileType(context))
    $registry.register(
      'formula_type',
      new GrouperFormulaMultipleCollaboratorsType(context)
    )

    // File preview types
    $registry.register('preview', new ImageFilePreview(context))
    $registry.register('preview', new AudioFilePreview(context))
    $registry.register('preview', new VideoFilePreview(context))
    $registry.register('preview', new PDFBrowserFilePreview(context))
    $registry.register('preview', new GoogleDocFilePreview(context))

    $registry.register('viewAggregation', new MinViewAggregationType(context))
    $registry.register('viewAggregation', new MaxViewAggregationType(context))
    $registry.register('viewAggregation', new SumViewAggregationType(context))
    $registry.register(
      'viewAggregation',
      new AverageViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new MedianViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new StdDevViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new VarianceViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new EarliestDateViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new LatestDateViewAggregationType(context)
    )
    $registry.register('viewAggregation', new CountViewAggregationType(context))
    $registry.register(
      'viewAggregation',
      new EmptyCountViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new NotEmptyCountViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new CheckedCountViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new NotCheckedCountViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new EmptyPercentageViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new NotEmptyPercentageViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new CheckedPercentageViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new NotCheckedPercentageViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new UniqueCountViewAggregationType(context)
    )
    $registry.register(
      'viewAggregation',
      new DistributionViewAggregationType(context)
    )

    $registry.register('formViewMode', new FormViewFormModeType(context))

    $registry.register(
      'databaseDataProvider',
      new FieldsDataProviderType(context)
    )

    // notifications
    $registry.register(
      'notification',
      new CollaboratorAddedToRowNotificationType(context)
    )
    $registry.register(
      'notification',
      new FormSubmittedNotificationType(context)
    )
    $registry.register(
      'notification',
      new UserMentionInRichTextFieldNotificationType(context)
    )
    $registry.register(
      'notification',
      new WebhookDeactivatedNotificationType(context)
    )
    $registry.register(
      'notification',
      new WebhookPayloadTooLargedNotificationType(context)
    )

    $registry.register(
      'rowModalSidebar',
      new HistoryRowModalSidebarType(context)
    )

    $registry.register('onboarding', new DatabaseOnboardingType(context))
    $registry.register(
      'onboarding',
      new DatabaseScratchTrackOnboardingType(context)
    )
    $registry.register(
      'onboarding',
      new DatabaseScratchTrackFieldsOnboardingType(context)
    )
    $registry.register('onboarding', new DatabaseImportOnboardingType(context))

    $registry.register(
      'databaseOnboardingStep',
      new ScratchDatabaseOnboardingStepType(context)
    )
    $registry.register(
      'databaseOnboardingStep',
      new ImportDatabaseOnboardingStepType(context)
    )
    $registry.register(
      'databaseOnboardingStep',
      new AirtableDatabaseOnboardingStepType(context)
    )

    $registry.register(
      'onboardingTrackFields',
      new DatabaseScratchTrackProjectFieldsOnboardingType(context)
    )
    $registry.register(
      'onboardingTrackFields',
      new DatabaseScratchTrackTeamFieldsOnboardingType(context)
    )
    $registry.register(
      'onboardingTrackFields',
      new DatabaseScratchTrackTaskFieldsOnboardingType(context)
    )
    $registry.register(
      'onboardingTrackFields',
      new DatabaseScratchTrackCampaignFieldsOnboardingType(context)
    )
    $registry.register(
      'onboardingTrackFields',
      new DatabaseScratchTrackCustomFieldsOnboardingType(context)
    )

    $registry.register(
      'configureDataSync',
      new SyncedFieldsConfigureDataSyncType(context)
    )
    $registry.register(
      'configureDataSync',
      new SettingsConfigureDataSyncType(context)
    )
    $registry.register(
      'configureDataSync',
      new SyncHistoryConfigureDataSyncType(context)
    )

    $registry.register('guidedTour', new DatabaseGuidedTourType(context))

    $registry.registerNamespace('fieldContextItem')

    searchTypeRegistry.register(new DatabaseSearchType(context))
    searchTypeRegistry.register(new DatabaseTableSearchType(context))
    searchTypeRegistry.register(new DatabaseFieldSearchType(context))
    searchTypeRegistry.register(new DatabaseRowSearchType(context))
  },
})
